import { useCallback, useEffect, useMemo } from "react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";
import { createMST3010_R01, deleteMST3010_R01, downloadExcel_MST3010_R01, getMST3010_R01AllCount, getMST3010_R01List, getMST3010_R01One, updateMST3010_R01 } from "./Api_MST3010_R01";

import { validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { defaultMST3010_R01Dto, MST3010_R01Dto } from "./Dto_MST3010_R01";
import { defaultMST3010_R01Filter, MST3010_R01Filter, useMST3010_R01Store } from "./Store_MST3010_R01";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { AgGridReact } from "ag-grid-react";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	MST3010_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';

interface WorkplaceContentProps {	
	gridRef: React.RefObject<AgGridReact<MST3010_R01Dto> | null>;
	setMessage: (message: string) => void;
	
}

export const Content_MST3010_R01 = (props: WorkplaceContentProps) => {

	const {
		gridRef,
		setMessage,
	} = props;

	const {
		MST3010_R01Filter, setMST3010_R01Filter,
		MST3010_R01PastFilter, setMST3010_R01PastFilter,
		MST3010_R01List, setMST3010_R01List, removeMST3010_R01,
		selectedMST3010_R01, setSelectedMST3010_R01,
		editMST3010_R01, setEditMST3010_R01,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useMST3010_R01Store();
	const { 
		wmsCdList,
		yesNoList,
		compCdList,
		warehouseCdList,
		workProcessList,
		fetchWorkPlaceCdCode,
	} = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	// 권한 정보 로드
	useFetchActionbarAuth("MST3010_R01");
	
	const handleExcelClick = useCallback(async () => {

		setLoadingSpinner(true);

		const response = await downloadExcel_MST3010_R01(
			'',
			getParams(MST3010_R01Filter),
			MST3010_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '공정 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [MST3010_R01Filter, MST3010_R01List, setLoadingSpinner]);

	const getParams = (filter: Partial<MST3010_R01Filter>) => {
		return {
			workplaceNm: filter.workplaceNm,
		};
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST3010_R01AllCount(getParams(MST3010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST3010_R01Filter, setAllCount]);
	
	const fetchData = useCallback(async () => {

		setLoadingSpinner(true);
		setScrollTop(null);
		let data: MST3010_R01Dto[] = [];
		
		try {

			
			// 백엔드 서비스에서 검색 조건으로 필터링된 데이터 조회
			const response = await getMST3010_R01List(
				'search',
				getParams(MST3010_R01Filter),
				MST3010_R01List.length ?? 0
			);

			let newList = [...response.data.data ] as MST3010_R01Dto[];
			setMST3010_R01List(newList);

			const processAllCount = await renewalAllCount();
			
			// 데이터가 있으면 첫 번째 행 선택
			if (newList && newList.length > 0) {
				gridRefSelectedRowHandler(
					gridRef,
					setSelectedMST3010_R01,
					() => true,// () => !data.find(value => value.workplaceCd === selectedMST3010_R01?.workplaceCd)
				);
				setMST3010_R01PastFilter(MST3010_R01Filter);
				setLoadingSpinner(false);
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			} else {
				showWarning('데이터가 없습니다.');
				handleCreateClick();
				setSelectedMST3010_R01(undefined);
			}
		} catch (error) {
			setMST3010_R01List([]);
			setSelectedMST3010_R01(undefined);
		}
		handleCreateClick();

		return data;
	}, [MST3010_R01Filter, MST3010_R01List, gridRef, setMST3010_R01List, setMST3010_R01PastFilter, setSelectedMST3010_R01, selectedMST3010_R01, setScrollTop])

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	//출고창고 지정 목록 - 자재 + 공정
	const outWarehouseCdListToUse = useMemo(() => {
		let outLogicCenter = wmsCdList[0].minorCd;
		if(validateString(selectedMST3010_R01?.logicCenter || '') && selectedMST3010_R01?.logicCenter){
			outLogicCenter = selectedMST3010_R01?.logicCenter;
		} else if(validateString(editMST3010_R01?.logicCenter || '') && editMST3010_R01?.logicCenter){
			outLogicCenter = editMST3010_R01?.logicCenter;
		}

		return warehouseCdList.filter(value => (value.logicCenter === outLogicCenter
			&& (value.materialYn === 'Y' || value.manufactYn === 'Y' )
		));
	}, [selectedMST3010_R01, editMST3010_R01, warehouseCdList, wmsCdList]);

	//입고창고 지정 목록 제품 + 공정
	const inWarehouseCdListToUse = useMemo(() => {
		let inLogicCenter = wmsCdList[0].minorCd;
		if(validateString(selectedMST3010_R01?.logicCenter || '') && selectedMST3010_R01?.logicCenter){
			inLogicCenter = selectedMST3010_R01?.logicCenter;
		} else if(validateString(editMST3010_R01?.logicCenter || '') && editMST3010_R01?.logicCenter){
			inLogicCenter = editMST3010_R01?.logicCenter;
		}

		return warehouseCdList.filter(value => (value.logicCenter === inLogicCenter
			&& (value.productYn === 'Y' || value.manufactYn === 'Y' )
		));
	}, [selectedMST3010_R01, editMST3010_R01, warehouseCdList, wmsCdList]);

	//불량창고 지정 목록
	const badWarehouseCdListToUse = useMemo(() => {
		let badLogicCenter = wmsCdList[0].minorCd;
		if(validateString(selectedMST3010_R01?.logicCenter || '') && selectedMST3010_R01?.logicCenter){
			badLogicCenter = selectedMST3010_R01?.logicCenter;
		} else if(validateString(editMST3010_R01?.logicCenter || '') && editMST3010_R01?.logicCenter){
			badLogicCenter = editMST3010_R01?.logicCenter;
		}

		return warehouseCdList.filter(value => (value.logicCenter === badLogicCenter
			&& value.badYn === 'Y'
		));
	}, [selectedMST3010_R01, editMST3010_R01, warehouseCdList, wmsCdList]);
	

	const fetchOne = useCallback(async () => {
		if (!selectedMST3010_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getMST3010_R01One(selectedMST3010_R01.workplaceCd);

			setEditMST3010_R01({ 
				...defaultMST3010_R01Row, ...selectedMST3010_R01, ...response.data.data,
				inWarehouse: validateString(response.data.data.inWarehouse) ? response.data.data.inWarehouse : inWarehouseCdListToUse[0]?.warehouseCd,
				outWarehouse: validateString(response.data.data.outWarehouse) ? response.data.data.outWarehouse : outWarehouseCdListToUse[0]?.warehouseCd,
				badWarehouse: validateString(response.data.data.badWarehouse) ? response.data.data.badWarehouse : badWarehouseCdListToUse[0]?.warehouseCd,
			});
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [MST3010_R01Filter, setEditMST3010_R01, selectedMST3010_R01]);

	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultMST3010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST3010_R01_ROW_VALIDATE, {
				compCdList,
				wmsCdList,
				workProcessList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultMST3010_R01Dto };
		}

		let logicCenter = wmsCdList[0].minorCd;
		if (selectedMST3010_R01?.logicCenter) {
			logicCenter = wmsCdList.filter(value => value.minorCd === selectedMST3010_R01?.logicCenter)[0].minorCd;
		}

		return {
			...defaultMST3010_R01Dto,
			compCd: compCdList[0].compCd,
			workProcess: workProcessList[0].minorCd,
			useYn: yesNoList[0]?.minorCd || 'Y',
			orderYn: yesNoList[0]?.minorCd || 'N',
			barcodePrint: yesNoList[0]?.minorCd || 'N',
			outsourcingYn: yesNoList[0]?.minorCd || 'N',
			scanHistory: yesNoList[0]?.minorCd || 'N',
			faultyYn: yesNoList[0]?.minorCd || 'N',
			logicCenter,
			inWarehouse: '',
			outWarehouse: '',
			badWarehouse: '',
			inserted: true,
		};
	}, [
		defaultMST3010_R01Dto, yesNoList, wmsCdList, compCdList, workProcessList,
		selectedMST3010_R01, validateCommonCodeLists,
	]);

	const handleCreateClick = useCallback(async () => {
		setSelectedMST3010_R01(undefined);
		setEditMST3010_R01({
			...defaultMST3010_R01Row(),
		});
	}, [])
	

	// (조회페이지 이기에 미완성)
	const handleDeleteClick = useCallback(async () => {
		if (!selectedMST3010_R01) {
			showWarning('선택된 작업장이 없습니다.');
			return;
		}

		if (!window.confirm('정말로 삭제하시겠습니까?')) {
			return;
		}

		try {
			await deleteMST3010_R01(selectedMST3010_R01.workplaceCd);
			refetchUpdateList('delete', defaultMST3010_R01Row(), selectedMST3010_R01, 'workplaceCd', MST3010_R01List, setMST3010_R01List);
			handleCreateClick();
			showSuccess(`${selectedMST3010_R01.workplaceCd} 작업장 삭제가 완료되었습니다.`);
			
		} catch (error) {
			showWarning('삭제 중 오류가 발생했습니다.');
		}
	}, [selectedMST3010_R01])

	const validateProcessData = (data: MST3010_R01Dto) => {
		if (!validateString(data.workplaceCd))		{ showWarning("작업장코드를 입력해주세요.");		return false; }
		if (!validateString(data.workplaceNm))		{ showWarning("공정명을 입력해주세요.");		return false; }
		if (!validateString(data.compCd))			{ showWarning("사업장을 입력해주세요.");	return false; }
		if (!validateString(data.workProcess))		{ showWarning("공정유형을 입력해주세요.");		return false; }
		if (!validateString(data.levelCd))			{ showWarning("라벨코드를 입력해주세요.");		return false; }
		if (validateString(data.outWarehouse))		{ 
			//출고창고가 정해졌을때 창고번호 검증
			const outWarehouseInfo = outWarehouseCdListToUse.find(value => value.warehouseCd === data.outWarehouse);
			if(!outWarehouseInfo) {
				showWarning("출고창고의 정보가 출고창고명과 일치하지 않습니다.");		return false;
			}
		}else{
			showWarning("출고창고를 선택해주세요.");		return false;
		}
		if (validateString(data.inWarehouse))		{ 
			//입고창고가 정해졌을때 창고번호 검증
			const inWarehouseInfo = inWarehouseCdListToUse.find(value => value.warehouseCd === data.inWarehouse);
			if(!inWarehouseInfo) {
				showWarning("입고창고의 정보가 입고창고명과 일치하지 않습니다.");		return false;
			}
		}else{
			showWarning("입고창고를 선택해주세요.");		return false;
		}
		if (validateString(data.badWarehouse))		{ 
			//불량창고가 정해졌을때 창고번호 검증
			const badWarehouseInfo = badWarehouseCdListToUse.find(value => value.warehouseCd === data.badWarehouse);
			if(!badWarehouseInfo) {
				showWarning("불량창고의 정보가 불량창고명과 일치하지 않습니다.");		return false;
			}
		}else{
			showWarning("불량창고를 선택해주세요.");		return false;
		}

		return true;
	}

	const validateInsertData = (data: MST3010_R01Dto) => {
		const processValid = validateProcessData(data);
		if(!processValid) return false;

		const processList = MST3010_R01List.filter(item => item.valueIndex !== data.valueIndex);
		if(!processList) return false;

		const validateWorkplace = processList.filter(item => item.workplaceCd === data.workplaceCd);
		if(validateWorkplace.length > 0) { showWarning('작업장코드가 중복됩니다.'); return false; }

		const validateWorkplaceNm = processList.filter( item => item.compCd === data.compCd && item.workplaceNm === data.workplaceNm );
		if(validateWorkplaceNm.length > 0) { showWarning('공정명이 중복됩니다.'); return false; }

		return true;
	}

	const validateUpdateData = (data: MST3010_R01Dto) => { 
		const processValid = validateProcessData(data); 
		if(!processValid) return false; 
		
		return true; 
	}
	
	
	// (조회페이지 이기에 미완성)
	const handleSaveClick = useCallback(async () => {

		let processName = '';
		
		let isPostProcess = (!selectedMST3010_R01)? true : false;
		let isPatchProcess = (selectedMST3010_R01) ? true : false;

		if(isPostProcess ) { if(!validateInsertData(editMST3010_R01)) return; }
		if(isPatchProcess ) { if(!validateUpdateData(editMST3010_R01)) return; }

		let responseData: MST3010_R01Dto = { ...defaultMST3010_R01Dto };

		try {
			if (isPatchProcess) {
				processName = 'patch';
				const response = await updateMST3010_R01(editMST3010_R01);
				responseData = response.data.data[0];
				// 백엔드에서 WorkplaceEntity 반환하므로 response 존재 여부만 확인
				if (response) {
					showSuccess(`${editMST3010_R01.workplaceCd} 작업장 수정이 완료되었습니다.`);
					setMessage(`${editMST3010_R01.workplaceCd} 작업장 수정이 완료되었습니다.`);
				}
			} else if (isPostProcess) {
				processName = 'post';
				const response = await createMST3010_R01(editMST3010_R01);
				responseData = response.data.data[0];
				// 백엔드에서 WorkplaceEntity 반환하므로 response 존재 여부만 확인
				if (response) {
					showSuccess(`${editMST3010_R01.workplaceCd} 작업장 등록이 완료되었습니다.`);
					setMessage(`${editMST3010_R01.workplaceCd} 작업장 등록이 완료되었습니다.`);
				}
			}

			setTimeout(() => {
				fetchWorkPlaceCdCode();
				setMessage("");
			}, 5000)

			// 저장 후 목록 새로고침
			if(editMST3010_R01) {
				refetchUpdateList(processName, responseData, editMST3010_R01, 'workplaceCd', MST3010_R01List, setMST3010_R01List);
				setSelectedMST3010_R01(responseData);
			}
		} catch (error) {
			showWarning('저장 중 오류가 발생했습니다.');
		}

	}, [editMST3010_R01, selectedMST3010_R01]);	

	useEffect(() => {
		if (selectedMST3010_R01) {
			fetchOne();
		}else{
			setEditMST3010_R01(defaultMST3010_R01Row());
		}
	}, [selectedMST3010_R01]);

	return {
		handleExcelClick,
		fetchData,
		initContainerData,
		outWarehouseCdListToUse,
		inWarehouseCdListToUse,
		badWarehouseCdListToUse,
		defaultMST3010_R01Row,
		handleCreateClick,
		handleSaveClick,
		handleDeleteClick,
	}
} 